import { z } from "zod";
import { PairModeZ, PairStatusZ, PairZ } from "../domain/pair";

export const CreatePairReqZ = z.object({
  id: z.string().min(1).max(32),
  firmId: z.string().min(1).max(32),
  longAccount: z.string().min(1).max(32),
  shortAccount: z.string().min(1).max(32),
  symbol: z.string().min(1).max(16),
  lots: z.number().positive(),
});
export type CreatePairReq = z.infer<typeof CreatePairReqZ>;

/**
 * One side of a hedge pair — the joined account row plus enrichment from
 * firms/firm_profiles + DO live state. The Worker returns this shape so
 * the dashboard doesn't need to merge listAccounts() / listFirms() /
 * DO-state in the browser.
 */
export const PairAccountSideZ = z.object({
  accountId: z.string(),
  side: z.enum(["long", "short"]),
  firmName: z.string(),
  firmLogo: z.string(),
  platformType: z.string(),
  adapter: z.enum(["ea", "playwright"]),
  /** step1 / step2 / funded — drives whether the P&L shown is "real" money
   *  (funded) or "challenge progress" (step1/step2). During a challenge
   *  the trader hasn't earned anything yet; the UI must not display
   *  P&L as realised profit. */
  phase: z.enum(["step1", "step2", "funded"]),
  /** USD price the operator paid for this challenge seat. When both legs
   *  reach funded status the pair card deducts these from gross P&L to
   *  show true net profit ("how much did I actually make?"). Null when
   *  unknown (free retry or gift). */
  challengeFee: z.number().nullable(),
  /** Live values from the HedgePair Durable Object — 0 when DO has no state yet. */
  equity: z.number(),
  pnl: z.number(),
  dailyDdUsed: z.number(),
  dailyDdLimit: z.number(),
  totalDdUsed: z.number(),
  totalDdLimit: z.number(),
  online: z.boolean(),
  /** Pre-sleep snapshot the bot captured on last sleep. Lets the
   *  symbol-pair page show "Asleep with these positions" instead of
   *  going dark. Lazy-imported via the account-pairs module to avoid a
   *  cycle. Optional so existing callers parse legacy responses. */
  lastSleepSnapshot: z
    .object({
      equity: z.number().nullable(),
      balance: z.number().nullable(),
      marginLevel: z.number().nullable(),
      openPnl: z.number(),
      closedPnl: z.number().nullable(),
      drawdownUsed: z.number().nullable(),
      dailyUsed: z.number().nullable(),
      openPositions: z.array(
        z.object({
          ticket: z.number().int(),
          symbol: z.string(),
          side: z.enum(["buy", "sell"]),
          lots: z.number(),
          entry: z.number(),
          stopLoss: z.number().nullable(),
          takeProfit: z.number().nullable(),
          openPnl: z.number(),
        }),
      ),
      positionCount: z.number().int().nonnegative(),
      protectedCount: z.number().int().nonnegative(),
      capturedAt: z.number().int(),
    })
    .nullable()
    .optional(),
  lastSleepSnapshotAt: z.number().int().nullable().optional(),
});
export type PairAccountSide = z.infer<typeof PairAccountSideZ>;

/** Last decision summary for the pair card — null if Claude hasn't run yet. */
export const PairLastDecisionZ = z.object({
  tier: z.enum(["haiku", "sonnet"]),
  action: z.string(),
  complianceStatus: z.string().nullable(),
  reason: z.string(),
  confidence: z.number().nullable(),
  createdAt: z.number(),
});
export type PairLastDecision = z.infer<typeof PairLastDecisionZ>;

/**
 * Rich pair shape returned by GET /api/pairs. Matches the dashboard's
 * HedgePair type at apps/dashboard/src/components/pair-card.tsx so the
 * page can render with no client-side mapping.
 */
export const PairListItemZ = z.object({
  id: z.string(),
  /** Opaque URL-safe identifier. The dashboard uses this in /pairs/:slug
   *  so the raw id (which leaks firm names like "auto-blueberry-…-maven-…")
   *  never appears in a URL. Internal code keeps using `id`. */
  slug: z.string(),
  /** Parent account_pair's slug. Needed by the dashboard to build
   *  nested URLs (PATCH /account-pairs/:slug/pairs/:pairSlug/lots,
   *  etc). Nullable only for legacy rows predating account-pair
   *  linking — new pairs always have one. */
  accountPairSlug: z.string().nullable(),
  /** Snapshot of the parent account_pair's sleep/wake state. Shared
   *  by every symbol pair under the same account_pair (browsers are
   *  per-account, not per-symbol). Optional so existing callers
   *  parse legacy responses. */
  pairSleep: z
    .object({
      mode: z.enum(["auto", "manual"]),
      manualState: z.enum(["awake", "asleep"]),
      currentState: z.enum(["awake", "asleep", "partial", "unknown"]),
      autoSleepProtected: z.boolean(),
    })
    .nullable()
    .optional(),
  symbol: z.string(),
  lots: z.number(),
  contractSize: z.number().nullable().optional(),
  leverage: z.number().nullable().optional(),
  phase: z.enum(["compound", "harvest", "pivot"]),
  status: PairStatusZ,
  /** Resolved AI mode for this symbol = modeOverride ?? parent.aiMode.
   *  HedgePair DO consumes this via `pair_meta.mode`; the API keeps
   *  it in sync whenever either input changes. */
  mode: PairModeZ,
  /** Operator-pinned override. When non-null, this symbol ignores the
   *  parent account_pair's aiMode. NULL = inherit. */
  modeOverride: PairModeZ.nullable(),
  /** Parent account_pair's `ai_mode` — what this symbol resolves to
   *  when modeOverride is null. Surfaced so the single-symbol page
   *  can label the Inherit option (e.g. "Inherit (Auto)") without a
   *  second fetch. Null only for legacy rows without an account_pair. */
  parentAiMode: PairModeZ.nullable(),
  netPnl: z.number(),
  cycleDay: z.number().int(),
  longAccount: PairAccountSideZ,
  shortAccount: PairAccountSideZ,
  lastDecision: PairLastDecisionZ.nullable(),
  connectionStatus: z.enum(["connecting", "open", "closed"]),
  createdAt: z.number(),
});
export type PairListItem = z.infer<typeof PairListItemZ>;

export const ListPairsResZ = z.object({
  pairs: z.array(PairListItemZ),
});
export type ListPairsRes = z.infer<typeof ListPairsResZ>;

/** Single-pair detail view — same item plus the full DO state if available. */
export const PairDetailResZ = z.object({
  pair: PairListItemZ,
  /** Raw HedgePair DO state JSON, passed through for the detail page. */
  doState: z.unknown().nullable(),
});
export type PairDetailRes = z.infer<typeof PairDetailResZ>;

/** Lower-level domain shape — kept for Worker-internal use. */
export const PairRowZ = PairZ;
export type PairRow = z.infer<typeof PairRowZ>;

// ─── Pair mode controls ────────────────────────────────────────

/**
 * Per-symbol mode override. NULL clears the pin and the symbol falls
 * back to the parent account_pair's aiMode. Anything else pins this
 * symbol regardless of parent.
 */
export const SetPairModeOverrideReqZ = z.object({
  modeOverride: PairModeZ.nullable(),
});
export type SetPairModeOverrideReq = z.infer<typeof SetPairModeOverrideReqZ>;

export const ActivatePairReqZ = z.object({
  mode: PairModeZ.default("manual"),
});
export type ActivatePairReq = z.infer<typeof ActivatePairReqZ>;

// ─── Operator-approval queue (semi_auto mode) ──────────────────

export const PairSuggestionActionZ = z.enum(["open_trade", "close_pair"]);
export type PairSuggestionAction = z.infer<typeof PairSuggestionActionZ>;

export const PairSuggestionStatusZ = z.enum([
  "pending",
  "approved",
  "rejected",
  "expired",
  "superseded",
]);
export type PairSuggestionStatus = z.infer<typeof PairSuggestionStatusZ>;

export const PairSuggestionZ = z.object({
  id: z.number().int(),
  pairId: z.string(),
  pairSlug: z.string(),
  action: PairSuggestionActionZ,
  payload: z.unknown(),
  reasoning: z.string().nullable(),
  status: PairSuggestionStatusZ,
  decisionId: z.string().nullable(),
  createdAt: z.number().int(),
  expiresAt: z.number().int(),
  decidedAt: z.number().int().nullable(),
  decidedBy: z.string().nullable(),
});
export type PairSuggestion = z.infer<typeof PairSuggestionZ>;

export const ListPairSuggestionsResZ = z.object({
  suggestions: z.array(PairSuggestionZ),
});
export type ListPairSuggestionsRes = z.infer<typeof ListPairSuggestionsResZ>;
